import { CalendarDays } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { formatDateValue, maskDate, parseDateValue } from '../lib/dateInput';

export function DateInput({
  value,
  onChange,
  onValidChange,
  ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  onValidChange?: (valid: boolean) => void;
  ariaLabel?: string;
}) {
  const nativeInput = useRef<HTMLInputElement>(null);
  const focused = useRef(false);
  const [draft, setDraft] = useState(() => formatDateValue(value));
  const [invalid, setInvalid] = useState(false);

  useEffect(() => {
    if (!focused.current) setDraft(formatDateValue(value));
  }, [value]);

  const updateDraft = (nextDraft: string) => {
    const masked = maskDate(nextDraft);
    setDraft(masked);
    if (!masked) {
      setInvalid(false);
      onValidChange?.(true);
      onChange('');
      return;
    }
    const parsed = parseDateValue(masked);
    const complete = masked.length === 10;
    setInvalid(complete && !parsed);
    onValidChange?.(Boolean(parsed));
    if (parsed) onChange(parsed);
  };

  return (
    <div className={`date-input${invalid ? ' is-invalid' : ''}`}>
      <input
        aria-invalid={invalid}
        aria-label={ariaLabel}
        inputMode="numeric"
        onBlur={() => {
          focused.current = false;
          const parsed = parseDateValue(draft);
          if (draft && !parsed) {
            setInvalid(true);
            onValidChange?.(false);
          }
        }}
        onChange={(event) => updateDraft(event.target.value)}
        onClick={() => nativeInput.current?.showPicker?.()}
        onFocus={() => { focused.current = true; }}
        placeholder="дд.мм.гггг"
        type="text"
        value={draft}
      />
      <button
        aria-label={`Открыть календарь${ariaLabel ? `: ${ariaLabel}` : ''}`}
        className="date-input__picker"
        onClick={() => nativeInput.current?.showPicker?.()}
        type="button"
      >
        <CalendarDays size={14} />
      </button>
      <input
        aria-hidden="true"
        className="date-input__native"
        onChange={(event) => {
          onChange(event.target.value);
          setDraft(formatDateValue(event.target.value));
          setInvalid(false);
          onValidChange?.(true);
        }}
        ref={nativeInput}
        tabIndex={-1}
        type="date"
        value={value}
      />
    </div>
  );
}
